import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import classNames from 'classnames';
import { Feature } from 'config/features';

import { useFeature } from 'hooks/useFeature';
import { useHib } from 'hooks/useHib';
import { QaIdentifier, useMultipleQa } from 'hooks/useQa';
import { useRatingLabel } from 'hooks/useRatingConfig';

import { getDecimalNumber } from 'utility/Format/Number';
import { HotelDetailType } from 'utility/Provide/Url';
import { trackGa4Event } from 'utility/Track/trackingHelper';

import { RatingSummaryType } from '../__helper';
import Tooltip from '../Tooltip';
import { AccommodationProps, RatingValueSide } from '../types';

import styles from './Accommodation.module.scss';

const Accommodation = ({
    valueSide,
    classListObject,
    trackingData,
    hotelId,
    hibUrl,
    rating,
    onClick,
    openRatingLayer = true,
    type,
    showSlogan = false,
}: AccommodationProps) => {
    const { t } = useTranslation();
    const { average, count } = rating;
    const [qaAttrCount, qaAttrLink, qaAttrAverage] = useMultipleQa([
        QaIdentifier.User_Rating_Count,
        QaIdentifier.User_Rating_Link,
        QaIdentifier.User_Rating_Average,
    ]);
    const isHibOnPageActive = useFeature(Feature.HibOnPage);
    const { openPopup } = useHib();

    const onClickCallback = useCallback(
        (e: React.MouseEvent) => {
            trackingData &&
                trackGa4Event('ratingLayer_click_open', {
                    click_position: trackingData.click_position,
                    hotel_id: hotelId,
                    hotel_name: trackingData.hotel_name,
                });
            if (!isHibOnPageActive) {
                openPopup(hotelId, HotelDetailType.Rating);
            }
            onClick?.(e);
        },
        [hotelId, onClick, trackingData, isHibOnPageActive, openPopup]
    );

    const ratingLabel = useRatingLabel(average);
    const dataHibRating =
        openRatingLayer && isHibOnPageActive
            ? {
                  'data-hib-detail':
                      openRatingLayer && hibUrl
                          ? JSON.stringify({
                                url: hibUrl,
                                content: 'all',
                            })
                          : undefined,
                  'data-hotel-id': hotelId,
                  'data-hib-page': HotelDetailType.Rating,
                  'data-rating-count': count,
                  'aria-hidden': false,
                  role: 'button',
              }
            : {};
    return (
        <div
            {...qaAttrLink}
            className={classNames(
                styles.Accommodation,
                {
                    [styles.ValueRight]: valueSide === RatingValueSide.Right,
                    'js-hib-link js-hib-rating-guests-link js-rating-overlay':
                        openRatingLayer,
                },
                classListObject?.Accommodation
            )}
            onClick={onClickCallback}
            aria-hidden="true"
            {...dataHibRating}
        >
            <div
                {...qaAttrAverage}
                className={classNames(
                    styles.Value,
                    classListObject?.RatingValue
                )}
            >
                {average
                    ? getDecimalNumber(average, average > 9.9 ? 0 : 1)
                    : '-'}
            </div>
            <div
                className={classNames(
                    styles.Text,
                    classListObject?.RatingText,
                    {
                        [styles.TextWrapper]:
                            type === RatingSummaryType.Combined,
                    }
                )}
            >
                {count > 0 ? (
                    <>
                        <b>{ratingLabel}</b>
                        {type !== RatingSummaryType.TrustYou && (
                            <span
                                {...qaAttrCount}
                                className={classNames(
                                    styles.Count,
                                    classListObject?.Count
                                )}
                            >
                                {t('TRANS-rating[i]', {
                                    postProcess: 'interval',
                                    count: count,
                                    formattedCount: getDecimalNumber(count, 0),
                                })}
                            </span>
                        )}
                    </>
                ) : (
                    <span className={styles.None}>{ratingLabel}</span>
                )}
                {showSlogan && type !== RatingSummaryType.Guest && (
                    <div className={styles.TrustYouText}>
                        <span>{t('TRANS-reviewed-ratings')}</span>
                        <span>
                            {type === RatingSummaryType.Combined &&
                                ` • ${t('TRANS-based-on-verified-ty-ratings')}`}
                            {type === RatingSummaryType.TrustYou &&
                                ` • ${t(
                                    'TRANS-based-on-n-verified-ty-ratings',
                                    {
                                        postProcess: 'interval',
                                        formattedCount: getDecimalNumber(
                                            count,
                                            0
                                        ),
                                    }
                                )}`}
                            <Tooltip
                                type={RatingSummaryType.TrustYou}
                                customClassList={{ Icon: styles.Icon }}
                            />
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};
export default Accommodation;
